"use client";

// ──────────────────────────────────────────────────────────────────────────
//  Analytics — thin wrapper around the underlying tool (PostHog today,
//  could be Umami / Plausible / anything tomorrow). Everything in the
//  app calls these wrapper functions, NEVER the SDK directly. That way
//  swapping tools is one file change.
//
//  Privacy stance:
//    - Cookieless. Persistence: 'memory' for anonymous users; we
//      identify by bnet_id only after sign-in.
//    - No PII in event props. Battle.net IDs are hashed before send.
//    - Opt-out automatic if NEXT_PUBLIC_POSTHOG_KEY is unset (dev /
//      preview builds capture nothing).
//
//  Event taxonomy: see docs/ANALYTICS.md.
// ──────────────────────────────────────────────────────────────────────────

import posthog from "posthog-js";

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
// All PostHog traffic tunnels through our own origin (`/ingest/*`)
// to dodge ad-blockers. The destination is set in `next.config.ts`'s
// rewrites; PostHog's UI links still need the real host so we expose
// it separately as `ui_host` below.
const HOST = "/ingest";
const UI_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";

let started = false;

/** Initialize the tracker. Idempotent — safe to call from multiple
 *  mount points. No-op if NEXT_PUBLIC_POSTHOG_KEY isn't set, which
 *  is the case in dev unless explicitly configured. */
export function initAnalytics(): void {
  if (started) return;
  if (typeof window === "undefined") return;
  if (!KEY) {
    console.info("[analytics] NEXT_PUBLIC_POSTHOG_KEY unset, tracking disabled");
    return;
  }
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // Used for cross-PostHog navigation links (e.g. recording URLs).
      // Stays the real host; api_host is the proxy.
      ui_host: UI_HOST,
      // Pin SDK behavior to a known-good date so a future posthog-js
      // release can't silently change defaults on us. Bump after
      // testing a new version end-to-end.
      defaults: "2026-01-30",
      // Only create a "person" record after we identify() the user
      // post-sign-in. Anonymous visitors stay event-only; saves the
      // free-tier MAU quota for users who actually authenticated.
      person_profiles: "identified_only",
      // Cookieless. PostHog stores anonymous IDs in sessionStorage so
      // visitors get a stable handle within a tab but no cross-session
      // identifier — keeps us in the GDPR "no consent banner needed"
      // category.
      persistence: "memory",
      // Auto-capture pageviews on history changes (Next.js client navigation).
      capture_pageview: true,
      // Strip query strings from page URLs — avoids leaking ?admin=1 etc.
      sanitize_properties: (props) => {
        if (typeof props.$current_url === "string") {
          try {
            const u = new URL(props.$current_url);
            props.$current_url = `${u.origin}${u.pathname}`;
          } catch { /* ignore */ }
        }
        return props;
      },
      loaded: () => {
        // Log so DevTools confirms init completed. Cheap one-line
        // smoke test we can grep for in console.
        console.info("[analytics] posthog ready", HOST);
      },
    });
    started = true;
    installGlobalErrorHandlers();
  } catch (err) {
    // Without this catch a bad config option silently swallows init
    // and `window.posthog` stays undefined — the failure mode that
    // bit us on first launch.
    console.error("[analytics] posthog.init() threw", err);
  }
}

/** Wire window-level handlers so EVERY unhandled JS error surfaces in
 *  PostHog as an $exception. Combined with explicit captureException()
 *  calls in apiFetch (HTTP errors aren't JS exceptions and never reach
 *  these handlers), this gives us full coverage of:
 *
 *    - synchronous throws not caught by any try/catch
 *    - rejected promises with no .catch
 *    - HTTP 4xx (≠401/404) + 5xx via apiFetch
 *    - network failures via apiFetch
 *
 *  Idempotent: handlers attach once via `started` check above. */
function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (ev) => {
    const err = ev.error instanceof Error
      ? ev.error
      : new Error(ev.message || "Unknown window error");
    captureException(err, {
      kind: "window_error",
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    const err = reason instanceof Error
      ? reason
      : new Error(typeof reason === "string" ? reason : "Unhandled promise rejection");
    captureException(err, {
      kind: "unhandled_rejection",
      reason_type: typeof reason,
    });
  });
}

/** Send an explicit exception to PostHog. Safe to call before init —
 *  silently no-ops if the SDK hasn't loaded (e.g. during early page
 *  bootstrap or in dev with no key). */
export function captureException(
  err: unknown,
  props?: Record<string, unknown>,
): void {
  if (!started) return;
  try {
    posthog.captureException(err instanceof Error ? err : new Error(String(err)), props);
  } catch { /* never let logging itself break the page */ }
}

/** Tie subsequent events to a specific signed-in user. Bnet IDs are
 *  hashed client-side before send so PostHog never sees the raw value. */
export async function identifyUser(bnetID: string, props?: { region?: string }) {
  if (!started) return;
  const hashed = await sha256(bnetID);
  posthog.identify(hashed, props);
}

/** Forget the user — call from logout. */
export function resetIdentity() {
  if (!started) return;
  posthog.reset();
}

/** Fire a custom event. Names use snake_case for grep-ability. */
export function track(event: string, props?: Record<string, unknown>) {
  if (!started) return;
  posthog.capture(event, props);
}

// ── helpers ──────────────────────────────────────────────────────────────

async function sha256(s: string): Promise<string> {
  // SubtleCrypto in browsers; for Node-side calls (none currently) this
  // throws — that's fine, identify is browser-only by design.
  const buf = new TextEncoder().encode(s);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
